import { useCallback, useRef } from 'react'
import { MAX_HISTORY } from '../constants'

/**
 * useUndoRedo — Manages the canvas state history (nodes + edges).
 *
 * Pattern: ref-based history to avoid unnecessary re-renders.
 * canUndo/canRedo are computed via getters on refs (React Flow re-renders
 * the buttons via prop drilling, not via internal state of this hook).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useUndoRedo(nodes: any[], edges: any[], setNodes: (nodes: any[]) => void, setEdges: (edges: any[]) => void) {
  const historyRef = useRef<{ nodes: unknown[]; edges: unknown[] }[]>([])
  const indexRef   = useRef(-1)
  const skipRef    = useRef(false) // prevents push after undo/redo

  const rerender = useCallback(() => {
    // No state here — buttons read canUndo/canRedo via getter below
  }, [])

  const pushState = useCallback(() => {
    if (skipRef.current) { skipRef.current = false; return }

    const snapshot = {
      nodes: JSON.parse(JSON.stringify(nodes)),
      edges: JSON.parse(JSON.stringify(edges)),
    }

    // Discard redo history when creating a new state
    historyRef.current = historyRef.current.slice(0, indexRef.current + 1)
    historyRef.current.push(snapshot)

    if (historyRef.current.length > MAX_HISTORY) historyRef.current.shift()
    indexRef.current = historyRef.current.length - 1

    rerender()
  }, [nodes, edges, rerender])

  const undo = useCallback(() => {
    if (indexRef.current <= 0) return
    indexRef.current--
    const { nodes: n, edges: e } = historyRef.current[indexRef.current]
    skipRef.current = true
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setNodes(n as any[])
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setEdges(e as any[])
    rerender()
  }, [setNodes, setEdges, rerender])

  const redo = useCallback(() => {
    if (indexRef.current >= historyRef.current.length - 1) return
    indexRef.current++
    const { nodes: n, edges: e } = historyRef.current[indexRef.current]
    skipRef.current = true
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setNodes(n as any[])
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setEdges(e as any[])
    rerender()
  }, [setNodes, setEdges, rerender])

  return {
    pushState,
    undo,
    redo,
    get canUndo() { return indexRef.current > 0 },
    get canRedo() { return indexRef.current < historyRef.current.length - 1 },
  }
}
